import { useState } from 'react';

import { RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import { ErrorState } from '@/modules/common/components/organisms/error-state';
import { FullPageLoader } from '@/modules/common/components/organisms/file/full-page-loader';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { AuthenticatedRoutes } from '@/router/routes';

import { SettingsFooter, SettingsMobileMenu, SettingsNavigation } from '../components';
import { useAvailableSettings } from '../hooks';
import { SettingsFooterState } from '../types/settings-footer';

export const SettingsPage = () => {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const navigate = useNavigate();
  const { getErrorMessage } = useApiError();
  const {
    data: availableSettings,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useAvailableSettings();

  const [footerState, setFooterState] = useState<SettingsFooterState>({
    isVisible: false,
    isEnabled: false,
    isLoading: false,
    onSave: () => {},
  });
  const [contentMaxWidth, setContentMaxWidth] = useState<string | undefined>(undefined);

  if (isLoading) {
    return (
      <div className="relative flex h-full w-full">
        <FullPageLoader message={t('common:loading')} fullViewport={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full">
        <ErrorState
          message={getErrorMessage(error)}
          className="flex-1"
          actions={[
            {
              label: t('common:retry'),
              onClick: () => refetch(),
              variant: 'default',
              icon: <RefreshCw className="h-4 w-4" />,
              isLoading: isRefetching,
            },
            {
              label: t('common:close'),
              onClick: () => navigate(AuthenticatedRoutes.Planner),
              variant: 'outline',
              icon: <X className="h-4 w-4" />,
            },
          ]}
        />
      </div>
    );
  }

  if (!availableSettings) {
    return null;
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="border-b border-border bg-surface px-4 py-3 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <h1 className="text-2xl font-semibold text-foreground">{t('title')}</h1>
      </div>

      {/* Mobile menu button - only visible on mobile */}
      <div className="border-b border-border px-4 py-2 md:hidden">
        <SettingsMobileMenu availableSettings={availableSettings} />
      </div>

      <div className="flex flex-1 gap-4 overflow-hidden">
        {/* Desktop navigation - hidden on mobile */}
        <div className="hidden md:block">
          <SettingsNavigation availableSettings={availableSettings} />
        </div>

        <div className="relative flex flex-1 flex-col overflow-y-auto p-4">
          <div className={contentMaxWidth ?? 'max-w-4xl'}>
            <Outlet context={{ setFooterState, setContentMaxWidth }} />
          </div>
        </div>
      </div>

      <SettingsFooter state={footerState} />
    </div>
  );
};
